import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrencyDollarIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { IUserModel, CreateAvatarTrainRequest } from '@packages/global';
import { useCoinStore } from '../../stores/coinStore';
import { ModelSelector } from '../common/form';
import VideoUpload from '../common/VideoUpload';
import AvatarTabs from './AvatarTabs';
import AvatarVideoGuide from './AvatarVideoGuide';

interface AvatarTrainFormProps {
  trainForm: CreateAvatarTrainRequest;
  trainModels: IUserModel[];
  trainModelsLoading?: boolean;
  onTrainFormChange: (data: Partial<CreateAvatarTrainRequest>) => void;
  onTrainSubmit: (e: React.FormEvent) => void;
}

export default function AvatarTrainForm({
  trainForm, trainModels, trainModelsLoading, onTrainFormChange, onTrainSubmit,
}: AvatarTrainFormProps) {
  const { t } = useTranslation(['avatar', 'common']);
  const { account } = useCoinStore();
  const [showGuide, setShowGuide] = useState(false);

  const selectedModel = trainModels.find(m => m.id === trainForm.modelId);
  const coinPrice = selectedModel?.coinPrice ?? 0;
  const hasEnoughCoins = (account?.balance ?? 0) >= coinPrice;

  const inputCls = 'w-full bg-bg-form border border-border-subtle rounded-lg px-3 py-2 text-gray-50 placeholder-gray-500 focus:border-border-default transition-colors outline-none';
  const labelCls = 'block text-sm text-gray-400 mb-1';

  return (
    <div className="w-80 lg:w-96 bg-bg-form border-r border-border-subtle flex flex-col overflow-hidden flex-shrink-0">
      <AvatarTabs />

      <div className="flex-1 overflow-y-auto p-5">
        <form onSubmit={onTrainSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>{t('avatar:train.model')}</label>
            <ModelSelector
              models={trainModels}
              selectedModelId={trainForm.modelId}
              onChange={modelId => onTrainFormChange({ modelId })}
              loading={trainModelsLoading}
              placeholder={t('avatar:train.selectModel')}
            />
          </div>

          <div>
            <label className={labelCls}>{t('avatar:train.name')}</label>
            <input required value={trainForm.name}
              onChange={e => onTrainFormChange({ name: e.target.value })}
              placeholder={t('avatar:train.namePlaceholder')}
              className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>{t('avatar:train.gender')}</label>
            <select value={trainForm.gender}
              onChange={e => onTrainFormChange({ gender: e.target.value })}
              className={`${inputCls} cursor-pointer`}>
              <option value="female">{t('avatar:train.genderFemale')}</option>
              <option value="male">{t('avatar:train.genderMale')}</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls}>{t('avatar:train.videoUrl')}</label>
              <button type="button" onClick={() => setShowGuide(true)}
                className="flex items-center gap-1 py-0.5 px-2 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors cursor-pointer text-xs font-medium">
                <InformationCircleIcon className="w-3 h-3" />
                <span>{t('avatar:guide.requirementsTitle', '视频素材要求')}</span>
              </button>
            </div>
            <VideoUpload
              value={trainForm.videoUrl}
              onChange={url => onTrainFormChange({ videoUrl: url })}
              placeholder={t('avatar:train.videoUrlPlaceholder')}
            />
          </div>

          <button type="submit"
            disabled={trainModelsLoading || !hasEnoughCoins}
            className="w-full bg-primary hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-lg font-medium transition-colors cursor-pointer mt-2">
            {t('avatar:train.submit')}
          </button>

          {coinPrice > 0 && (
            <div className="flex items-center justify-between text-xs px-1 mt-2">
              <div className="flex items-center gap-1 text-gray-500">
                <CurrencyDollarIcon className="w-3.5 h-3.5" />
                <span>{t('avatar:train.cost')} <span className="font-mono text-primary">{coinPrice}</span></span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">{t('avatar:train.balance')}</span>
                <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-error'}`}>
                  {account?.balance ?? 0}
                </span>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Video Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowGuide(false)}>
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4"
            onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowGuide(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-bg-elevated/80 hover:bg-bg-elevated text-gray-400 hover:text-gray-200 transition-colors cursor-pointer z-10">
              <XMarkIcon className="w-5 h-5" />
            </button>
            <AvatarVideoGuide />
          </div>
        </div>
      )}
    </div>
  );
}
